import { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { fetchOrders } from 'actions/OrderActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { uniqueBy } from 'lib/array/arrayUtils'
import { haveOrdersBeenFetched, isItemCancelled } from 'lib/order/orderUtils'
import { getUpcomingOrders } from 'selectors/orderSelectors'
import AddedExperienceBookingTile from './AddedExperienceBookingTile'
import AddedFlightBookingTile from './AddedFlightBookingTile'
import AddedHotelBookingTile from './AddedHotelBookingTile'
import { sumOccupancies } from './bookingOccupancyUtils'
import { parseBookingContext, type BookedFlightReference, type BookingContextSection } from 'tripPlanner/utils/bookingContext'

interface BaseAddedBookingTileData {
  orderId: string
  itemId: string
}

interface AddedHotelBookingTileData extends BaseAddedBookingTileData {
  bookingType: 'hotel'
  offerId?: string
  propertyName: string
  checkInDate?: string
  checkOutDate?: string
  imageId?: string
  occupancy?: App.Occupants
}

interface AddedExperienceBookingTileData extends BaseAddedBookingTileData {
  bookingType: 'experience'
  offerId: string
  propertyName?: string
  checkInDate?: string
  time?: string
}

interface AddedFlightBookingTileData extends BaseAddedBookingTileData {
  bookingType: 'flight'
}

export type AddedBookingTileData =
  | AddedHotelBookingTileData
  | AddedExperienceBookingTileData
  | AddedFlightBookingTileData

const TilesViewport = styled.div`
  align-self: flex-end;
  width: 100%;
  max-width: ${rem(740)};
`

const TilesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${rem(10)};
  width: 100%;
`

function toAddedBookingTile(entry: BookingContextSection | BookedFlightReference): AddedBookingTileData {
  switch (entry.kind) {
    case 'hotel':
      return {
        bookingType: 'hotel',
        orderId: entry.orderId,
        itemId: entry.bookingItemId,
        offerId: entry.offerId,
        propertyName: entry.property,
        checkInDate: entry.checkIn,
        checkOutDate: entry.checkOut,
      }
    case 'experience':
      return {
        bookingType: 'experience',
        orderId: entry.orderId,
        itemId: entry.bookingItemId,
        offerId: entry.offerId,
        propertyName: entry.experience,
        checkInDate: entry.date,
        time: entry.time,
      }
    case 'flight':
      return { bookingType: 'flight', orderId: entry.orderId, itemId: entry.bookingItemId }
    case 'bookedFlight':
      return { bookingType: 'flight', orderId: entry.orderId, itemId: entry.orderFlightItemId }
  }
}

function getAddedBookingTileKey(tile: AddedBookingTileData): string {
  return `${tile.bookingType}:${tile.orderId}:${tile.itemId}`
}

export function extractAddedBookingTilesFromMessage(
  message: string | undefined,
): Array<AddedBookingTileData> {
  if (!message) {
    return []
  }

  const tiles = parseBookingContext(message).map(toAddedBookingTile)
  return uniqueBy(tiles, getAddedBookingTileKey)
}

function hydrateHotelBookingTile(
  tile: AddedHotelBookingTileData,
  orders: Array<App.Order>,
): AddedHotelBookingTileData {
  const matchingOrder = orders.find(order => order.id === tile.orderId)
  if (!matchingOrder) {
    return tile
  }

  const matchingOrderItem = matchingOrder.items.find(
    item => !isItemCancelled(item) && item.id === tile.itemId,
  )

  if (matchingOrderItem) {
    const occupancy = tile.occupancy ?? (matchingOrderItem.reservation ? {
      adults: matchingOrderItem.reservation.adults,
      children: matchingOrderItem.reservation.children,
      infants: matchingOrderItem.reservation.infants,
    } : undefined)

    return {
      ...tile,
      offerId: tile.offerId ?? matchingOrderItem.offerId,
      propertyName:
        matchingOrderItem.offer.property?.name ??
        matchingOrderItem.offer.name ??
        tile.propertyName,
      checkInDate: tile.checkInDate ?? matchingOrderItem.reservation?.startDate,
      checkOutDate: tile.checkOutDate ?? matchingOrderItem.reservation?.endDate,
      imageId:
        tile.imageId ??
        matchingOrderItem.offer.property?.roomType?.images?.[0]?.id,
      occupancy,
    }
  }

  const matchingBedbankItem = matchingOrder.bedbankItems.find(
    item => !isItemCancelled(item) && item.id === tile.itemId,
  )

  if (matchingBedbankItem) {
    return {
      ...tile,
      offerId: tile.offerId ?? matchingBedbankItem.offer.id,
      propertyName:
        matchingBedbankItem.offer.property?.name ??
        matchingBedbankItem.offer.name ??
        tile.propertyName,
      imageId: tile.imageId ?? matchingBedbankItem.offer.image?.id,
      occupancy: tile.occupancy ?? sumOccupancies(matchingBedbankItem.rooms.map(room => room.occupancy)),
    }
  }

  return tile
}

export function hydrateAddedBookingTiles(
  tiles: Array<AddedBookingTileData>,
  orders: Array<App.Order>,
): Array<AddedBookingTileData> {
  return tiles.map((tile) => {
    if (tile.bookingType !== 'hotel') {
      return tile
    }

    return hydrateHotelBookingTile(tile, orders)
  })
}

interface Props {
  tiles: Array<AddedBookingTileData>
}

function AddedBookingTiles({ tiles }: Props) {
  const dispatch = useAppDispatch()
  const upcomingOrders = useAppSelector(getUpcomingOrders)
  const haveFetchedUpcomingOrders = useAppSelector(state =>
    haveOrdersBeenFetched(state, 'upcoming'),
  )
  const hasHotelTiles = useMemo(
    () => tiles.some(tile => tile.bookingType === 'hotel'),
    [tiles],
  )
  const hydratedTiles = useMemo(
    () => hydrateAddedBookingTiles(tiles, upcomingOrders),
    [tiles, upcomingOrders],
  )

  useEffect(() => {
    if (!hasHotelTiles || haveFetchedUpcomingOrders) {
      return
    }

    dispatch(fetchOrders({ status: 'upcoming' }))
  }, [dispatch, hasHotelTiles, haveFetchedUpcomingOrders])

  if (tiles.length === 0) {
    return null
  }

  return (
    <TilesViewport>
      <TilesContainer>
        {hydratedTiles.map((tile) => {
          const key = getAddedBookingTileKey(tile)

          switch (tile.bookingType) {
            case 'flight':
              return (
                <AddedFlightBookingTile
                  key={key}
                  itemId={tile.itemId}
                  orderId={tile.orderId}
                />
              )
            case 'experience':
              return (
                <AddedExperienceBookingTile
                  key={key}
                  experienceId={tile.offerId}
                  propertyName={tile.propertyName}
                  checkInDate={tile.checkInDate}
                  time={tile.time}
                />
              )
            case 'hotel':
              return (
                <AddedHotelBookingTile
                  key={key}
                  propertyName={tile.propertyName}
                  checkInDate={tile.checkInDate}
                  checkOutDate={tile.checkOutDate}
                  imageId={tile.imageId}
                  occupancy={tile.occupancy}
                />
              )
          }
        })}
      </TilesContainer>
    </TilesViewport>
  )
}

export default AddedBookingTiles
